
import React from 'react';
import { Icon } from '@iconify/react';
import { MappedLeadData } from '../../types';
import { Card, CardHeader, SectionLabel, Tag, SimpleTabContent } from '../common/Atomic';

interface ProfileIntelTabProps {
  data: MappedLeadData;
}

const PSYCHOLOGICAL_FRAMEWORK_EXPLANATIONS = {
    enneagram: 'Motivation pattern: what drives this lead, what they avoid, and how they may react under pressure.',
    pcm: 'Communication style: the channel, tone, and stress signals this lead is most likely to respond to.',
    mbti: 'Four thinking preferences: I/E is energy, S/N is information style, T/F is decision style, and J/P is planning style.',
    archetype: 'Business persona: the role this lead naturally plays in decisions, plus the risk when that role is overused.',
};

const MBTI_LETTER_MEANINGS: Record<string, { term: string; description: string }> = {
    I: { term: 'Introversion', description: 'Prefers reflection, written context, and quieter focus.' },
    E: { term: 'Extraversion', description: 'Prefers discussion, quick interaction, and external energy.' },
    S: { term: 'Sensing', description: 'Trusts facts, practical details, and what is already proven.' },
    N: { term: 'Intuition', description: 'Looks for patterns, possibilities, and future implications.' },
    T: { term: 'Thinking', description: 'Prioritizes logic, evidence, and objective tradeoffs.' },
    F: { term: 'Feeling', description: 'Prioritizes values, people impact, and relationship harmony.' },
    J: { term: 'Judging', description: 'Likes structure, decisions, timelines, and clear next steps.' },
    P: { term: 'Perceiving', description: 'Likes flexibility, options, exploration, and room to adapt.' },
};

const getMbtiLetters = (type?: string | null) => type?.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 4).split('') || [];

const FrameworkHelpLabel: React.FC<{ label: string; explanation: string; accentClass?: string }> = ({
    label,
    explanation,
    accentClass = 'text-[#AEB8D4]',
}) => (
    <div className="group/help relative mb-2 inline-flex items-center gap-1.5">
        <SectionLabel label={label} className="mb-0" />
        <button
            type="button"
            className={`inline-flex h-4 w-4 items-center justify-center rounded-full border border-white/10 bg-white/[0.04] ${accentClass} transition-all hover:border-white/20 hover:bg-white/[0.08] focus:outline-none focus-visible:border-cyan-300/40 focus-visible:ring-2 focus-visible:ring-cyan-300/15`}
            aria-label={`${label} explanation`}
        >
            <Icon icon="bx:info-circle" className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
        <div
            role="tooltip"
            className="pointer-events-none absolute left-0 top-6 z-40 w-64 max-w-[calc(100vw-3rem)] translate-y-1 rounded-2xl border border-white/10 bg-[#070A0F]/95 p-3 text-[11px] leading-5 text-gray-200 opacity-0 shadow-[0_20px_60px_rgba(0,0,0,0.45)] backdrop-blur-xl transition-all duration-200 group-hover/help:translate-y-0 group-hover/help:opacity-100 group-focus-within/help:translate-y-0 group-focus-within/help:opacity-100"
        >
            <div className="mb-1 text-[9px] font-black uppercase tracking-[0.16em] text-white/40">What this means</div>
            {explanation}
        </div>
    </div>
);

export const ProfileIntelTab: React.FC<ProfileIntelTabProps> = ({ data }) => {
    const intel = data.rawProfileIntel;
    const psych = intel?.psychological_profile;
    const dashboard = intel?.confidence_dashboard;
    const cultural = intel?.cultural_intelligence;
    const social = intel?.social_intelligence;
    const bigFive = psych?.big_five_profile;
    const mbtiLetters = getMbtiLetters(psych?.mbti_type);
    const behavioral = bigFive?.behavioral_predictions || {};
    
    // New Data Points
    const buyer = intel?.buyer_archetype;
    const influence = intel?.influence_receptivity;
    const learning = intel?.learning_style;
    const biases = intel?.cognitive_bias_profile;
    const generation = intel?.generational_profile;
    const disc = psych?.disc_profile;

    return (
        <SimpleTabContent>
             <div className="space-y-6">
                {/* Generational Profile (New) */}
                {generation && (
                    <Card>
                        <div className="flex flex-col md:flex-row items-center gap-6">
                            <div className="flex-shrink-0 text-center md:text-left">
                                <SectionLabel label="Generation" />
                                <div className="text-3xl font-bold text-white mb-1">{generation.generation.replace(/_/g, ' ')}</div>
                                <div className="text-sm text-gray-500">{generation.estimated_age_range} Years Old</div>
                            </div>
                            <div className="w-px h-16 bg-white/5 hidden md:block"></div>
                            <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-4">
                                <div>
                                    <SectionLabel label="Tech Adoption" />
                                    <div className="text-white font-medium capitalize">{generation.generational_characteristics?.tech_adoption_style}</div>
                                </div>
                                <div>
                                    <SectionLabel label="Values" />
                                    <div className="flex flex-wrap gap-1">
                                        {generation.generational_characteristics?.work_values?.map((v: string, i: number) => (
                                            <span key={i} className="text-xs text-gray-300 bg-white/5 px-2 py-0.5 rounded">{v}</span>
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    <SectionLabel label="Strategy" />
                                    <div className="text-xs text-gray-400 leading-tight">{generation.generational_outreach_strategy}</div>
                                </div>
                            </div>
                        </div>
                    </Card>
                )}

                {/* Confidence Dashboard */}
                {dashboard && (
                    <Card>
                         <div className="flex items-center justify-between mb-6">
                            <h3 className="text-xl font-bold text-white">Profile Confidence</h3>
                            <div className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#5260FF] to-[#15FBFF]">
                                {dashboard.overall_profile_confidence}%
                            </div>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                             {Object.entries(dashboard.confidence_breakdown || {}).map(([key, val]: any) => (
                                 <div key={key} className="p-4 bg-white/[0.02] rounded-2xl">
                                     <SectionLabel label={key.replace(/_/g, ' ').replace(' profile', '')} />
                                     <div className="w-full bg-white/5 rounded-full h-1.5 mb-2">
                                         <div className={`h-1.5 rounded-full ${val > 75 ? 'bg-[#5260FF]' : val > 50 ? 'bg-[#15FBFF]' : 'bg-red-500'}`} style={{ width: `${val}%` }}></div>
                                     </div>
                                     <div className="text-right text-xs text-white font-medium">{val}%</div>
                                 </div>
                             ))}
                        </div>
                    </Card>
                )}

                {/* Buyer DNA & Influence (New) */}
                {(buyer || influence) && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <Card className="lg:col-span-1 relative overflow-hidden group">
                             <div className="absolute top-0 right-0 w-32 h-32 bg-[#5260FF]/5 blur-2xl rounded-full"></div>
                             <CardHeader 
                                title="Buyer DNA" 
                                icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"/></svg>}
                                iconColor="text-[#15FBFF]"
                             />
                             <div className="space-y-6 relative z-10">
                                 {buyer?.type && (
                                     <div className="text-center p-4 bg-white/[0.02] rounded-xl">
                                         <SectionLabel label="Archetype" className="mb-2" />
                                         <div className="text-xl font-bold text-white mb-1">{buyer.type.replace(/_/g, ' ')}</div>
                                         <div className="text-xs text-gray-500">Confidence: {buyer.buyer_confidence}%</div>
                                     </div>
                                 )}
                                 <div className="space-y-4">
                                     <div className="flex justify-between items-center border-b border-white/5 pb-2">
                                         <span className="text-gray-400 text-sm">Motivation</span>
                                         <span className="text-white text-sm font-medium capitalize">{buyer?.buying_motivation?.replace(/_/g, ' ') || 'N/A'}</span>
                                     </div>
                                     <div className="flex justify-between items-center border-b border-white/5 pb-2">
                                         <span className="text-gray-400 text-sm">Proof Required</span>
                                         <span className="text-white text-sm font-medium capitalize">{buyer?.proof_required?.replace(/_/g, ' ') || 'N/A'}</span>
                                     </div>
                                     <div className="flex justify-between items-center">
                                         <span className="text-gray-400 text-sm">Adoption Curve</span>
                                         <span className="text-[#15FBFF] text-sm font-bold">{buyer?.innovation_adoption_curve_position || 'N/A'}</span>
                                     </div>
                                 </div>
                             </div>
                        </Card>

                        <Card className="lg:col-span-2">
                             <CardHeader 
                                title="Influence Receptivity" 
                                icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>}
                                iconColor="text-[#5260FF]"
                             />
                             <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                 <div className="space-y-4">
                                     <SectionLabel label="Cialdini's Levers" />
                                     {[
                                         { k: 'Authority', v: influence?.authority },
                                         { k: 'Commitment', v: influence?.commitment_consistency },
                                         { k: 'Social Proof', v: influence?.social_proof },
                                         { k: 'Reciprocity', v: influence?.reciprocity },
                                         { k: 'Liking', v: influence?.liking },
                                         { k: 'Scarcity', v: influence?.scarcity }
                                     ].map((item, i) => {
                                         const val = parseInt(item.v || '0');
                                         return (
                                             <div key={i} className="flex items-center gap-3">
                                                 <div className="w-24 text-xs text-gray-400 font-medium">{item.k}</div>
                                                 <div className="flex-1 bg-white/5 rounded-full h-1.5">
                                                     <div 
                                                        className={`h-1.5 rounded-full transition-all duration-500 ${val > 70 ? 'bg-[#5260FF]' : val > 40 ? 'bg-[#15FBFF]' : 'bg-gray-600'}`} 
                                                        style={{ width: `${val}%` }}
                                                     ></div>
                                                 </div>
                                                 <div className="w-8 text-xs text-white text-right">{val}%</div>
                                             </div>
                                         )
                                     })}
                                 </div>
                                 <div className="space-y-4">
                                      <div className="p-4 bg-white/[0.02] rounded-xl">
                                           <SectionLabel label="Primary Lever" />
                                           <div className="text-white font-bold mb-1">{influence?.primary_influence_lever}</div>
                                           <div className="text-xs text-gray-400">{influence?.influence_strategy}</div>
                                      </div>
                                      <div>
                                           <SectionLabel label="Resistance Points" className="mb-2" />
                                           <div className="flex flex-wrap gap-2">
                                               {influence?.resistance_points?.map((pt: string, i: number) => (
                                                   <Tag key={i} label={pt} color="text-red-400 bg-red-500/10" />
                                               ))}
                                           </div>
                                      </div>
                                 </div>
                             </div>
                        </Card>
                    </div>
                )}

                {/* Cognitive & Learning (New) */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <Card>
                         <CardHeader 
                            title="Learning Style" 
                            icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/></svg>}
                            iconColor="text-[#4FCDCF]"
                         />
                         <div className="grid grid-cols-2 gap-4 mb-6">
                             <div className="p-4 bg-white/[0.02] rounded-xl">
                                 <SectionLabel label="Primary Style" />
                                 <div className="text-lg text-white font-bold capitalize">{learning?.primary_style?.replace(/_/g, ' ') || 'N/A'}</div>
                             </div>
                             <div className="p-4 bg-white/[0.02] rounded-xl">
                                 <SectionLabel label="Format Pref" />
                                 <div className="text-lg text-white font-bold capitalize">{learning?.content_consumption_preference?.format_preference || 'N/A'}</div>
                             </div>
                         </div>
                         <div className="p-4 bg-white/[0.02] rounded-xl">
                             <SectionLabel label="Presentation Strategy" className="mb-2" />
                             <p className="text-gray-300 text-sm leading-relaxed">{learning?.presentation_strategy}</p>
                         </div>
                    </Card>

                    <Card>
                         <CardHeader
                            title="Cognitive Biases"
                            icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/></svg>}
                            iconColor="text-violet-400"
                         />
                         <div className="space-y-3 max-h-[250px] overflow-y-auto custom-scrollbar pr-2">
                             {biases?.predicted_objections?.map((obj: any, i: number) => (
                                 <div key={i} className="p-3 bg-white/[0.02] rounded-xl">
                                     <div className="flex justify-between items-start mb-1">
                                         <span className="text-xs font-bold text-violet-400 uppercase">{obj.bias_source}</span>
                                         <span className="text-[10px] text-gray-500">{obj.probability} risk</span>
                                     </div>
                                     <p className="text-sm text-gray-300 italic mb-2">"{obj.objection}"</p>
                                     <div className="text-xs text-green-400 font-medium flex items-start gap-1">
                                         <Icon icon="bx:bulb" className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                                         {obj.counter_strategy}
                                     </div>
                                 </div>
                             ))}
                             {!biases?.predicted_objections && <div className="text-gray-500 italic">No specific biases detected.</div>}
                         </div>
                    </Card>
                </div>

                {/* Cultural Intelligence (Existing - Compacted) */}
                {cultural && (
                    <Card>
                        <CardHeader 
                            title="Cultural Intelligence"
                            icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>}
                            iconColor="text-[#5260FF]"
                        />
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                            <div className="p-5 bg-white/[0.02] rounded-2xl">
                                <SectionLabel label="Primary Culture" />
                                <div className="text-xl text-white font-bold">{cultural.primary_culture}</div>
                            </div>
                            <div className="space-y-3">
                                <div className="flex justify-between items-center p-3 bg-white/[0.02] rounded-xl">
                                    <span className="text-gray-400 text-sm">Relationship Building</span>
                                    <span className="text-white text-sm font-semibold bg-white/5 px-2 py-1 rounded capitalize">{cultural.cultural_communication_norms?.relationship_building?.importance} Importance</span>
                                </div>
                                <div className="flex justify-between items-center p-3 bg-white/[0.02] rounded-xl">
                                    <span className="text-gray-400 text-sm">Business Formality</span>
                                    <span className="text-white text-sm font-semibold bg-white/5 px-2 py-1 rounded capitalize">{cultural.cultural_communication_norms?.business_communication?.formality_level}</span>
                                </div>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                             <div className="p-5 bg-emerald-900/10 rounded-2xl">
                                 <h4 className="mb-4 flex items-center gap-2 text-sm font-bold text-emerald-400">
                                     <Icon icon="bx:check-circle" className="h-4 w-4" aria-hidden="true" />
                                     Green Flags
                                 </h4>
                                 <ul className="space-y-2">
                                     {cultural.cultural_green_flags?.map((flag: string, i: number) => (
                                         <li key={i} className="text-sm text-gray-300 flex items-start gap-2">
                                             <span className="text-emerald-500 mt-1">•</span>
                                             {flag}
                                         </li>
                                     ))}
                                 </ul>
                             </div>
                             <div className="p-5 bg-red-900/10 rounded-2xl">
                                 <h4 className="mb-4 flex items-center gap-2 text-sm font-bold text-red-400">
                                     <Icon icon="bx:error-circle" className="h-4 w-4" aria-hidden="true" />
                                     Red Flags
                                 </h4>
                                 <ul className="space-y-2">
                                     {cultural.cultural_red_flags?.map((flag: string, i: number) => (
                                         <li key={i} className="text-sm text-gray-300 flex items-start gap-2">
                                             <span className="text-red-500 mt-1">•</span>
                                             {flag}
                                         </li>
                                     ))}
                                 </ul>
                             </div>
                        </div>
                    </Card>
                )}

                {/* Social Insights (Existing) */}
                {social?.content_analysis?.content_summary && (
                    <Card>
                         <CardHeader 
                            title="Social Insights"
                            icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"/></svg>}
                            iconColor="text-[#15FBFF]"
                         />
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                             <div className="p-4 bg-white/[0.02] rounded-xl">
                                 <div className="text-[10px] text-gray-500 uppercase font-bold mb-1">Engagement Style</div>
                                 <div className="text-white font-medium capitalize">{social.content_analysis.content_summary.engagement_style}</div>
                             </div>
                             <div className="p-4 bg-white/[0.02] rounded-xl">
                                 <div className="text-[10px] text-gray-500 uppercase font-bold mb-1">Posting Frequency</div>
                                 <div className="text-white font-medium capitalize">{social.content_analysis.content_summary.posting_frequency}</div>
                             </div>
                             <div className="p-4 bg-white/[0.02] rounded-xl">
                                 <div className="text-[10px] text-gray-500 uppercase font-bold mb-1">Conv. Readiness</div>
                                 <div className="text-white font-medium capitalize">{social.content_analysis.content_summary.conversation_readiness}</div>
                             </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                             <div>
                                 <div className="mb-2 flex items-center gap-1.5 text-red-300">
                                     <Icon icon="bx:error-circle" className="h-3.5 w-3.5" aria-hidden="true" />
                                     <SectionLabel label="Pain Themes" className="!mb-0" />
                                 </div>
                                 <div className="space-y-2">
                                     {social.content_analysis.content_summary.pain_themes?.map((theme: string, i: number) => (
                                         <div key={i} className="px-3 py-2 bg-white/[0.02] rounded-lg text-sm text-gray-300">{theme}</div>
                                     ))}
                                     {!social.content_analysis.content_summary.pain_themes?.length && <p className="text-gray-500 text-sm italic">None detected</p>}
                                 </div>
                             </div>
                             <div>
                                 <div className="mb-2 flex items-center gap-1.5 text-amber-200">
                                     <Icon icon="bx:star" className="h-3.5 w-3.5" aria-hidden="true" />
                                     <SectionLabel label="Aspiration Themes" className="!mb-0" />
                                 </div>
                                 <div className="space-y-2">
                                     {social.content_analysis.content_summary.aspiration_themes?.map((theme: string, i: number) => (
                                         <div key={i} className="px-3 py-2 bg-white/[0.02] rounded-lg text-sm text-gray-300">{theme}</div>
                                     ))}
                                     {!social.content_analysis.content_summary.aspiration_themes?.length && <p className="text-gray-500 text-sm italic">None detected</p>}
                                 </div>
                             </div>
                        </div>
                    </Card>
                )}

                {/* Deep Psychological Profile (Enriched) */}
                <Card>
                    <CardHeader 
                        title="Deep Psychological Profile"
                        icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/></svg>}
                        iconColor="text-[#4FCDCF]"
                    />

                    {/* Core Frameworks Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                        {/* Enneagram */}
                        {psych?.enneagram_type && (
                            <div className="p-5 rounded-2xl bg-white/[0.02] hover:border-[#5260FF]/30 transition-all group">
                                <FrameworkHelpLabel
                                    label="Enneagram"
                                    explanation={PSYCHOLOGICAL_FRAMEWORK_EXPLANATIONS.enneagram}
                                    accentClass="text-[#8D96FF]"
                                />
                                <div className="text-2xl font-bold text-white mb-1 group-hover:text-[#5260FF] transition-colors">{psych.enneagram_type}</div>
                                <div className="text-xs text-gray-400 mb-3">{psych.enneagram_confidence}% Confidence</div>
                                <div className="text-xs text-gray-500 leading-tight">{psych.core_motivation}</div>
                            </div>
                        )}
                        
                        {/* PCM */}
                        {psych?.pcm_type && (
                            <div className="p-5 rounded-2xl bg-white/[0.02] hover:border-[#15FBFF]/30 transition-all group">
                                <FrameworkHelpLabel
                                    label="PCM"
                                    explanation={PSYCHOLOGICAL_FRAMEWORK_EXPLANATIONS.pcm}
                                    accentClass="text-[#15FBFF]"
                                />
                                <div className="text-2xl font-bold text-white mb-1 group-hover:text-[#15FBFF] transition-colors">{psych.pcm_type}</div>
                                <div className="text-xs text-gray-400 mb-3">{psych.pcm_confidence}% Confidence</div>
                                <div className="text-xs text-gray-500 leading-tight">Channel: {psych.pcm_communication_channel || 'N/A'}</div>
                            </div>
                        )}

                        {/* MBTI */}
                        {psych?.mbti_type && (
                            <div className="p-5 rounded-2xl bg-white/[0.02] hover:border-[#7486FF]/30 transition-all group">
                                <FrameworkHelpLabel
                                    label="MBTI"
                                    explanation={PSYCHOLOGICAL_FRAMEWORK_EXPLANATIONS.mbti}
                                    accentClass="text-[#7486FF]"
                                />
                                <div className="text-2xl font-bold text-white mb-1 group-hover:text-[#7486FF] transition-colors">{psych.mbti_type}</div>
                                <div className="text-xs text-gray-400 mb-3">{psych.mbti_confidence}% Confidence</div>
                                {mbtiLetters.length > 0 && (
                                    <div className="mt-2 space-y-2">
                                        <div className="flex flex-wrap gap-1.5">
                                            {mbtiLetters.map((letter) => {
                                                const meaning = MBTI_LETTER_MEANINGS[letter];

                                                return (
                                                    <span
                                                        key={letter}
                                                        className="inline-flex items-center gap-1 rounded-lg bg-white/5 px-2 py-1 text-[10px] font-bold text-gray-200"
                                                        title={meaning ? `${letter} = ${meaning.term}. ${meaning.description}` : letter}
                                                    >
                                                        <span className="text-[#AEB8FF]">{letter}</span>
                                                        {meaning?.term}
                                                    </span>
                                                );
                                            })}
                                        </div>
                                        <div className="text-[10px] leading-4 text-gray-500">
                                            {mbtiLetters.map((letter) => {
                                                const meaning = MBTI_LETTER_MEANINGS[letter];
                                                return meaning ? `${letter} = ${meaning.term}` : letter;
                                            }).join(' | ')}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Archetype */}
                        {psych?.archetype && (
                            <div className="p-5 rounded-2xl bg-white/[0.02] hover:border-[#4FCDCF]/30 transition-all group">
                                <FrameworkHelpLabel
                                    label="Archetype"
                                    explanation={PSYCHOLOGICAL_FRAMEWORK_EXPLANATIONS.archetype}
                                    accentClass="text-[#4FCDCF]"
                                />
                                <div className="text-2xl font-bold text-white mb-1 group-hover:text-[#4FCDCF] transition-colors">{psych.archetype}</div>
                                <div className="text-xs text-gray-400 mb-3">{psych.archetype_confidence}% Confidence</div>
                                <div className="text-xs text-gray-500 leading-tight">Shadow: {psych.archetype_shadow || 'N/A'}</div>
                            </div>
                        )}
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                         {/* Big Five Traits */}
                         {bigFive && (
                             <div className="p-6 bg-white/[0.02] rounded-2xl">
                                 <h4 className="text-sm font-bold text-white uppercase tracking-wider mb-6 flex justify-between items-center">
                                     Personality Dimensions
                                     <Tag label={`${bigFive.ocean_confidence}% Conf.`} color="bg-[#5260FF]/10 text-[#5260FF]" />
                                 </h4>
                                 <div className="space-y-4">
                                     {[
                                         { label: 'Openness', val: parseInt(bigFive.openness) || 50, color: 'bg-blue-500' },
                                         { label: 'Conscientiousness', val: parseInt(bigFive.conscientiousness) || 50, color: 'bg-purple-500' },
                                         { label: 'Extraversion', val: parseInt(bigFive.extraversion) || 50, color: 'bg-green-500' },
                                         { label: 'Agreeableness', val: parseInt(bigFive.agreeableness) || 50, color: 'bg-orange-500' },
                                         { label: 'Neuroticism', val: parseInt(bigFive.neuroticism) || 50, color: 'bg-red-500' }
                                     ].map((trait, i) => (
                                         <div key={i}>
                                             <div className="flex justify-between text-xs mb-1.5">
                                                 <span className="text-gray-400 font-medium">{trait.label}</span>
                                                 <span className="text-white font-bold">{trait.val}%</span>
                                             </div>
                                             <div className="w-full h-2 bg-black/40 rounded-full overflow-hidden">
                                                 <div className={`h-full rounded-full ${trait.color} opacity-80`} style={{ width: `${trait.val}%` }}></div>
                                             </div>
                                         </div>
                                     ))}
                                 </div>
                             </div>
                         )}

                         {/* DISC & Behavioral */}
                         <div className="space-y-4">
                              {/* DISC Profile (New) */}
                              {disc && (
                                  <div className="p-6 bg-white/[0.02] rounded-2xl">
                                      <h4 className="text-sm font-bold text-white uppercase tracking-wider mb-4 flex justify-between">
                                          DISC Profile
                                          <span className="text-[#15FBFF]">{disc.disc_pattern}</span>
                                      </h4>
                                      <div className="grid grid-cols-2 gap-4">
                                          <div>
                                              <SectionLabel label="Primary" />
                                              <div className="text-white font-medium">{disc.primary_style}</div>
                                          </div>
                                          <div>
                                              <SectionLabel label="Secondary" />
                                              <div className="text-white font-medium">{disc.secondary_style}</div>
                                          </div>
                                          <div>
                                              <SectionLabel label="Conflict" />
                                              <div className="text-gray-400 text-sm">{disc.conflict_style}</div>
                                          </div>
                                          <div>
                                              <SectionLabel label="Risk" />
                                              <div className="text-gray-400 text-sm">{disc.risk_approach}</div>
                                          </div>
                                      </div>
                                  </div>
                              )}

                              {/* Behavioral Grid */}
                              <div className="p-6 bg-white/[0.02] rounded-2xl">
                                   <h4 className="text-sm font-bold text-white uppercase tracking-wider mb-4">Behavioral Predictions</h4>
                                   <div className="flex flex-wrap gap-2">
                                       {Object.entries(behavioral).map(([key, val]: any) => (
                                           <div key={key} className="px-3 py-2 bg-[#121212] rounded-lg flex items-center gap-2">
                                               <span className="text-[10px] text-gray-500 uppercase font-bold">{key.replace(/_/g, ' ')}</span>
                                               <span className="text-xs text-[#15FBFF] font-medium capitalize">{val.replace(/_/g, ' ')}</span>
                                           </div>
                                       ))}
                                       {Object.keys(behavioral).length === 0 && <p className="text-gray-500 text-sm italic">No predictions available.</p>}
                                   </div>
                              </div>
                         </div>
                    </div>
                </Card>
             </div>
        </SimpleTabContent>
    );
};
